import { AnimatePresence, m } from 'motion/react'
import { type ReactNode, useSyncExternalStore } from 'react'
import { defineMessages, useT } from '../i18n/index.ts'
import { cn } from '../lib/cn.ts'
import { IconAlert, IconCheckCircle, IconClose } from './Icon.tsx'
import { dur, ease } from './motion.ts'

/**
 * Toasts: a short note about something that just happened ("Link copied", "Saved").
 * The one owner of the toast list is this module; anything calls `toast()`, `<Toaster />`
 * (mounted once in App) renders it. Errors that need a decision belong in an Alert instead.
 */
export type ToastItem = {
	id: number
	message: ReactNode
	tone: 'neutral' | 'ok' | 'error'
	action?: { label: string; onClick: () => void }
}

const messages = defineMessages({
	en: { region: 'Notifications', dismiss: 'Dismiss' },
	ro: { region: 'Notificări', dismiss: 'Închide' },
	ru: { region: 'Уведомления', dismiss: 'Закрыть' },
	fr: { region: 'Notifications', dismiss: 'Fermer' },
	it: { region: 'Notifiche', dismiss: 'Chiudi' },
	kk: { region: 'Хабарландырулар', dismiss: 'Жабу' },
	es: { region: 'Notificaciones', dismiss: 'Cerrar' },
	pt: { region: 'Notificações', dismiss: 'Fechar' },
	de: { region: 'Benachrichtigungen', dismiss: 'Schließen' },
})

let items: ToastItem[] = []
let nextId = 1
const listeners = new Set<() => void>()
const emit = () => {
	for (const l of listeners) l()
}

function dismissToast(id: number) {
	items = items.filter((t) => t.id !== id)
	emit()
}

export function toast(
	message: ReactNode,
	opts: { tone?: ToastItem['tone']; action?: ToastItem['action']; ms?: number } = {},
) {
	const id = nextId++
	// At most three on screen; the oldest goes first.
	items = [...items.slice(-2), { id, message, tone: opts.tone ?? 'neutral', action: opts.action }]
	emit()
	setTimeout(() => dismissToast(id), opts.ms ?? 4000)
	return id
}

const subscribe = (l: () => void) => {
	listeners.add(l)
	return () => {
		listeners.delete(l)
	}
}
const snapshot = () => items

export function Toaster() {
	const t = useT(messages)
	const list = useSyncExternalStore(subscribe, snapshot, snapshot)
	return (
		<section
			aria-live="polite"
			aria-label={t.region}
			className="pointer-events-none fixed inset-x-0 bottom-0 z-(--z-toast) flex flex-col items-center gap-2 p-4 sm:items-start sm:p-6 print:hidden"
		>
			<AnimatePresence initial={false}>
				{list.map((item) => (
					<m.div
						key={item.id}
						layout
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0, transition: { duration: dur.slow, ease: ease.out } }}
						exit={{ opacity: 0, y: 6, transition: { duration: dur.quick, ease: ease.out } }}
						role={item.tone === 'error' ? 'alert' : 'status'}
						className="pointer-events-auto flex min-h-12 max-w-[min(28rem,calc(100vw-2rem))] items-center gap-3 rounded-btn bg-teal-950 py-2 pl-4 pr-2 text-[0.9375rem] text-white shadow-3"
					>
						{item.tone === 'ok' && <IconCheckCircle className="size-5 shrink-0 text-teal-200" />}
						{item.tone === 'error' && <IconAlert className="size-5 shrink-0 text-amber-200" />}
						<span className="min-w-0 flex-1 py-1">{item.message}</span>
						{item.action && (
							<button
								type="button"
								id={`toast-${item.id}-action`}
								onClick={() => {
									item.action?.onClick()
									dismissToast(item.id)
								}}
								className="h-9 shrink-0 rounded-input px-3 font-[560] text-teal-200 hover:bg-white/10"
							>
								{item.action.label}
							</button>
						)}
						<button
							type="button"
							id={`toast-${item.id}-x`}
							aria-label={t.dismiss}
							onClick={() => dismissToast(item.id)}
							className={cn(
								'grid size-9 shrink-0 place-items-center rounded-input text-white/70 hover:bg-white/10 hover:text-white',
							)}
						>
							<IconClose className="size-4" />
						</button>
					</m.div>
				))}
			</AnimatePresence>
		</section>
	)
}
